import React, { ButtonHTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { COLORS } from '../../constants/colors';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success' | 'outline' | 'like';
type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isFullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const StyledButton = styled.button<{
  variant: ButtonVariant;
  size: ButtonSize;
  $isFullWidth: boolean;
  $isLoading: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  position: relative;
  outline: none;
  width: ${props => (props.$isFullWidth ? '100%' : 'auto')};
  opacity: ${props => (props.disabled || props.$isLoading ? 0.7 : 1)};
  pointer-events: ${props => (props.disabled || props.$isLoading ? 'none' : 'auto')};

  @media (max-width: 768px) {
    padding: ${props => {
      return props.size === 'small' 
        ? '6px 12px' 
        : props.size === 'large' 
          ? '12px 24px' 
          : '8px 16px'; // medium/default
    }};
    
    font-size: 12px;
  }
  
  ${props => {
    switch (props.size) {
      case 'small':
        return css`
          padding: 8px 16px;
          font-size: 0.875rem;
        `;
      case 'large':
        return css`
          padding: 16px 24px;
          font-size: 1.125rem;
        `;
      default:
        return css`
          padding: 12px 20px;
          font-size: 1rem;
        `;
    }
  }}
  
  ${props => {
    switch (props.variant) {
      case 'secondary':
        return css`
          background-color: #e9ecef;
          color: #495057;
          border: none;
          
          &:hover:not(:disabled) {
            background-color: #dee2e6;
          }
          
          &:active:not(:disabled) {
            background-color: #ced4da;
          }
        `;
      case 'danger':
        return css`
          background-color: #fa5252;
          color: white;
          border: none;
          
          &:hover:not(:disabled) {
            background-color: #e03131;
          }
          
          &:active:not(:disabled) {
            background-color: #c92a2a;
          }
        `;
      case 'success':
        return css`
          background-color: #40c057;
          color: white;
          border: none;
          
          &:hover:not(:disabled) {
            background-color: #37b24d;
          }
          
          &:active:not(:disabled) {
            background-color: #2f9e44;
          }
        `;
      case 'outline':
        return css`
          background-color: transparent;
          color: ${COLORS.PRIMARY};
          border: 2px solid ${COLORS.PRIMARY};
          
          &:hover:not(:disabled) {
            background-color: rgba(66, 99, 235, 0.1);
          }
          
          &:active:not(:disabled) {
            background-color: rgba(66, 99, 235, 0.2);
          }
        `;
      case 'like':
        return css`
          background-color: transparent;
          color: ${COLORS.BACKGROUND};
          border: 2px solid ${COLORS.BACKGROUND};
          
          &:hover:not(:disabled) {
            background-color: rgba(66, 99, 235, 0.1);
          }
          
          &:active:not(:disabled) {
            background-color: rgba(66, 99, 235, 0.2);
          }
        `;
      default:
        return css`
          background-color: ${COLORS.PRIMARY};
          color: white;
          border: none;
          
          &:hover:not(:disabled) {
            background-color: ${COLORS.PRIMARY_DARK};
          }
          
          &:active:not(:disabled) {
            background-color: #364fc7;
          }
        `;
    }
  }}
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const IconWrapper = styled.span<{ isLeft: boolean }>`
  display: flex;
  align-items: center;
  margin-left: ${props => (props.isLeft ? '0' : '8px')};
  margin-right: ${props => (props.isLeft ? '8px' : '0')};
`;

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  isFullWidth = false,
  isLoading = false,
  leftIcon,
  rightIcon,
  ...props
}) => {
  return (
    <StyledButton
      variant={variant}
      size={size}
      $isFullWidth={isFullWidth}
      $isLoading={isLoading}
      {...props}
    >
      {isLoading && <LoadingSpinner />}
      {!isLoading && leftIcon && (
        <IconWrapper isLeft={true}>{leftIcon}</IconWrapper>
      )}
      {children}
      {!isLoading && rightIcon && (
        <IconWrapper isLeft={false}>{rightIcon}</IconWrapper>
      )}
    </StyledButton>
  );
};
